// search-enhanced.js — Lógica del sistema de búsqueda mejorado
// Depende de: search-synonyms.js / search-synonyms-en.js (window.SearchConfig)
//             theme.routes.search y theme.strings.search (layout/theme.liquid)
// No modifica theme.js — se integra via intercepción de fetch y eventos
//
// Este archivo es COMPARTIDO entre idiomas. Todo lo que varía por locale entra
// por window.SearchConfig (diccionarios, historyKey, queryPrefixes) o por
// theme.strings.search (textos). No debe haber español hardcodeado salvo como
// fallback de _t().

(function () {
  'use strict';

  var cfg = window.SearchConfig || {};
  var synonyms = cfg.synonyms || {};
  var typos    = cfg.typos    || {};
  var colors   = cfg.colors   || {};
  var historyKey = cfg.historyKey || 'hs-search-history';
  var maxHistory = cfg.maxHistoryItems || 5;   // cuántas búsquedas recientes se guardan (tope)
  var maxTermLen = cfg.maxHistoryTermLen || 60; // se descartan queries anómalamente largas

  // ── Textos traducidos ───────────────────────────────────────────────────
  // theme.strings.search se define en layout/theme.liquid. Los fallbacks en
  // español replican el comportamiento previo si no existiera.
  var S = (window.theme && window.theme.strings && window.theme.strings.search) || {};

  // Sustituye placeholders [nombre] en UN SOLO pase. Encadenar .replace() dejaría
  // que un término que contenga literalmente '[original]' se inyecte en la
  // sustitución siguiente.
  function _t(key, fallback, vars) {
    var s = S[key] || fallback;
    if (!vars) return s;
    return String(s).replace(/\[(\w+)\]/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : m;
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // SearchHistory — historial de búsquedas recientes en localStorage
  //   Buenas prácticas aplicadas:
  //   • Persistencia en localStorage bajo una sola clave (JSON array).
  //   • Deduplicado sin distinguir mayúsculas/acentos-visuales (case-insensitive).
  //   • Orden "más reciente primero" (unshift) y tope de N ítems (maxHistory).
  //   • Se ignoran términos vacíos, de <2 chars o >maxTermLen (evita basura/PII larga).
  //   • Borrado individual (remove) y total (clear).
  //   • Tolerante a fallos: try/catch en todo acceso a storage (modo incógnito, quota).
  // ════════════════════════════════════════════════════════════════════════
  var SearchHistory = {
    get: function () {
      try {
        var list = JSON.parse(localStorage.getItem(historyKey) || '[]');
        return Array.isArray(list) ? list : [];
      } catch (e) { return []; }
    },
    _save: function (list) {
      try { localStorage.setItem(historyKey, JSON.stringify(list.slice(0, maxHistory))); }
      catch (e) {}
    },
    add: function (term) {
      if (!term) return;
      term = term.trim();
      if (term.length < 2 || term.length > maxTermLen) return;
      var list = this.get().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      this._save(list);
    },
    remove: function (term) {
      var list = this.get().filter(function (t) { return t.toLowerCase() !== String(term).toLowerCase(); });
      if (list.length === 0) { this.clear(); return; }
      this._save(list);
    },
    clear: function () {
      try { localStorage.removeItem(historyKey); } catch (e) {}
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // SearchSynonyms — expansión de queries y corrección de typos
  // ════════════════════════════════════════════════════════════════════════
  var SearchSynonyms = {
    // Normaliza tildes y mayúsculas para comparaciones
    _norm: function (s) {
      return s.toLowerCase()
        .replace(/[áàâä]/g, 'a').replace(/[éèêë]/g, 'e')
        .replace(/[íìîï]/g, 'i').replace(/[óòôö]/g, 'o')
        .replace(/[úùûü]/g, 'u').replace(/ñ/g, 'n')
        .trim();
    },

    // Devuelve el término corregido si es un typo conocido, o null.
    // Para queries multi-palabra, intenta corregir cada palabra individualmente.
    correctTypo: function (q) {
      var lower = q.toLowerCase().trim();

      // Corrección del query completo primero
      if (typos[lower]) return typos[lower];
      var norm = this._norm(lower);
      for (var t in typos) {
        if (this._norm(t) === norm) return typos[t];
      }

      // Para queries multi-palabra: corregir cada palabra que sea un typo conocido
      if (lower.indexOf(' ') !== -1) {
        var self = this;
        var words = lower.split(/\s+/);
        var changed = false;
        var correctedWords = words.map(function (word) {
          if (typos[word]) { changed = true; return typos[word]; }
          var wNorm = self._norm(word);
          for (var t in typos) {
            if (self._norm(t) === wNorm) { changed = true; return typos[t]; }
          }
          return word;
        });
        if (changed) return correctedWords.join(' ');
      }

      return null;
    },

    // Expande un query con sinónimos usando OR.
    // Paso 1 — coincidencia exacta en el dict (soporta frases: 'wide leg', 'crop top').
    // Paso 2 — si no hay exacta y el query tiene varias palabras, busca la primera
    //          palabra que sea un color conocido y genera variantes ("vestido blanco"
    //          → "vestido blanco OR vestido ivory OR vestido hueso").
    expand: function (q) {
      q = q.trim();
      var lower = q.toLowerCase();
      var norm = this._norm(lower);

      // Paso 1: coincidencia exacta en synonyms
      var syns = synonyms[lower] || synonyms[norm];
      if (syns && syns.length > 0) {
        var terms = [q].concat(syns.slice(0, 3));
        var seen = {};
        terms = terms.filter(function (t) {
          var k = t.toLowerCase();
          if (seen[k]) return false;
          seen[k] = true;
          return true;
        });
        return terms.join(' OR ');
      }

      // Paso 2: expansión de color — solo para queries de una sola palabra.
      // Con múltiples palabras el OR multi-término devuelve 0 en Shopify.
      var self = this;
      var words = lower.split(/\s+/);
      if (words.length === 1) {
        var cs = colors[lower] || colors[self._norm(lower)];
        if (cs && cs.length > 0) {
          var colorSyns = cs.slice(0, 2);
          var colorTerms = [q].concat(colorSyns);
          var seen2 = {};
          colorTerms = colorTerms.filter(function (t) {
            var k = t.toLowerCase();
            if (seen2[k]) return false;
            seen2[k] = true;
            return true;
          });
          return colorTerms.join(' OR ');
        }
      }

      return q;
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // Autocomplete — sugerencias de TÉRMINOS mientras se escribe
  //   Diccionario: window.SearchConfig.terms = [[término, peso], ...]
  //   (generado por data/build-search-terms.mjs desde búsquedas reales + títulos)
  //   Se renderiza en #PredictiveTermSuggestions, junto a los productos predictivos.
  // ════════════════════════════════════════════════════════════════════════
  var Autocomplete = {
    index: [],       // [{display, norm, weight}]
    panel: null,     // #PredictiveTermSuggestions
    results: [],     // términos actualmente mostrados

    build: function () {
      var raw = cfg.terms || [];
      this.index = raw.map(function (t) {
        var display = Array.isArray(t) ? t[0] : t;
        var weight  = Array.isArray(t) ? (t[1] || 0) : 0;
        return { display: display, norm: SearchSynonyms._norm(display), weight: weight };
      });
    },

    // Devuelve hasta `limit` términos: 1) prefijo, 2) palabra-contiene, 3) typo corregido.
    suggest: function (query, limit) {
      limit = limit || 7;
      var q = SearchSynonyms._norm(_cleanQuery(query));
      if (!q || q.length < 2) return [];

      var exact = null, prefix = [], wordHit = [], seen = {};
      var wordRe = new RegExp('(^|\\s)' + _reEsc(q));
      for (var i = 0; i < this.index.length; i++) {
        var it = this.index[i];
        if (seen[it.norm]) continue;
        // El término idéntico al query no se sugiere (el usuario ya lo escribió),
        // pero se recuerda para no colapsar el panel si no hay nada más (ver abajo).
        if (it.norm === q) { exact = it; seen[it.norm] = 1; continue; }
        if (it.norm.indexOf(q) === 0) { prefix.push(it); seen[it.norm] = 1; }
        else if (wordRe.test(it.norm)) { wordHit.push(it); seen[it.norm] = 1; }
      }

      // Sin coincidencias directas → intentar corrección de typo y reintentar prefijo
      if (prefix.length === 0 && wordHit.length === 0) {
        var corr = SearchSynonyms.correctTypo(q);
        if (corr) {
          var cq = SearchSynonyms._norm(corr);
          for (var j = 0; j < this.index.length; j++) {
            var it2 = this.index[j];
            // En la rama de typo SÍ queremos sugerir el término corregido exacto
            if (seen[it2.norm]) continue;
            if (it2.norm.indexOf(cq) === 0) { prefix.push(it2); seen[it2.norm] = 1; }
          }
        }
      }

      var byWeight = function (a, b) { return b.weight - a.weight; };
      prefix.sort(byWeight);
      wordHit.sort(byWeight);
      var out = prefix.concat(wordHit);

      // Caso "pre order": el query coincide EXACTAMENTE con un término conocido y
      // no hay otras coincidencias. Si devolviéramos [], el panel se vaciaría y el
      // wrapper (que además puede quedar sin productos predictivos) desaparecería.
      // Mantener el término exacto conserva el panel abierto y da el affordance de
      // "buscar esto". Solo aplica cuando no hay ninguna otra sugerencia.
      if (out.length === 0 && exact) out = [exact];

      return out.slice(0, limit);
    },

    render: function (query) {
      if (!this.panel) return;
      var list = this.suggest(query, 7);
      this.results = list;

      if (list.length === 0) { this.clear(); return; }

      var form = document.getElementById('HeaderSearchForm');
      var typeInput = form && form.querySelector('input[name="type"]');
      var type = typeInput ? typeInput.value : 'product';
      var qn = SearchSynonyms._norm(_cleanQuery(query));

      var items = list.map(function (it) {
        var expanded = SearchSynonyms.expand(SearchSynonyms.correctTypo(it.display) || it.display);
        var href = _searchUrl(expanded, type);
        return '<li class="search-ac__item">' +
          '<a href="' + href + '" class="search-ac__link">' +
          '<svg class="search-ac__icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
          '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>' +
          '<span>' + _highlight(it.display, qn) + '</span></a></li>';
      }).join('');

      this.panel.innerHTML =
        '<p class="search-suggestions__heading">' + _esc(_t('suggestionsHeading', 'Sugerencias')) + '</p>' +
        '<ul class="search-suggestions__list search-ac__list">' + items + '</ul>';
      this.panel.style.display = '';
      this.ensureWrapperVisible();
    },

    // El reset() de theme.js (0 productos) oculta el wrapper y reabre el panel
    // de sugerencias vacío; si tenemos términos, revertimos ambos.
    ensureWrapperVisible: function () {
      var w = document.getElementById('PredictiveWrapper');
      if (w) w.classList.remove('hide');
      var sp = document.getElementById('SearchSuggestionsPanel');
      if (sp) sp.classList.add('hide');
    },

    // ¿El valor ACTUAL del input produce sugerencias? Se recalcula en vivo (índice
    // local, síncrono y barato) en vez de confiar en this.results, que lo puebla el
    // debounce de 100ms y puede estar desfasado respecto a lo que hay en pantalla.
    hasLiveSuggestions: function () {
      var form = document.getElementById('HeaderSearchForm');
      var input = form && form.querySelector('input[type="search"]');
      var val = input ? input.value : '';
      if (!val || _cleanQuery(val).length < 2) return false;
      return this.suggest(val, 7).length > 0;
    },

    clear: function () {
      this.results = [];
      if (this.panel) { this.panel.style.display = 'none'; this.panel.innerHTML = ''; }
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // _cleanQuery — limpia el query antes de procesar sinónimos y typos
  //   1. Quita asteriscos (usuarios a veces escriben "vestidos *")
  //   2. Convierte puntos-separador en espacio ("top.con.manga" → "top con manga")
  //   3. Colapsa espacios múltiples
  //   4. Elimina prefijos conversacionales ("busca / busco / buscando / quiero X" → "X")
  // ════════════════════════════════════════════════════════════════════════
  // Los prefijos vienen de SearchConfig.queryPrefixes (varían por idioma). Es
  // seguro usar un espacio literal en frases como 'quiero ver' porque las
  // sustituciones de arriba ya colapsaron los espacios múltiples antes de que
  // este regex se aplique. El fallback replica el regex original en español.
  var _prefixRe = (cfg.queryPrefixes && cfg.queryPrefixes.length)
    ? new RegExp('^(?:' + cfg.queryPrefixes.map(_reEsc).join('|') + ')\\s+', 'i')
    : /^(buscando|buscar?|busco|quiero\s+ver|quiero|dame|mu[eé]strame)\s+/i;
  function _cleanQuery(q) {
    return q
      .replace(/\*/g, ' ')
      .replace(/\./g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(_prefixRe, '')
      .trim();
  }

  // ════════════════════════════════════════════════════════════════════════
  // Intercepción de window.fetch — mejora búsqueda predictiva
  // ════════════════════════════════════════════════════════════════════════
  var _origFetch = window.fetch;
  window.fetch = function () {
    var args = Array.prototype.slice.call(arguments);
    var url = args[0];

    var isSuggest = typeof url === 'string' && url.indexOf('/search/suggest.json') !== -1;

    if (isSuggest) {
      try {
        var urlObj = new URL(url, window.location.origin);

        // theme.js pide '/search/suggest.json' hardcodeado (bug upstream de
        // Impulse 6.1), así que en /en el predictivo devolvía resultados en el
        // idioma primario. Reescribimos la ruta al prefijo activo. Va FUERA del
        // guard de abajo: aplica aunque la query venga vacía o ya expandida.
        // El check evita duplicar prefijo si algún día theme.js se arregla.
        var suggestPath = _searchRoute() + '/suggest.json';
        if (urlObj.pathname !== suggestPath) urlObj.pathname = suggestPath;

        var q = urlObj.searchParams.get('q') || '';

        // No re-procesar queries ya expandidas
        if (q && q.indexOf(' OR ') === -1) {
          var stripped = _cleanQuery(q);
          var corrected = SearchSynonyms.correctTypo(stripped);
          var base = corrected || stripped;
          var expanded = SearchSynonyms.expand(base);
          urlObj.searchParams.set('q', expanded);
        }

        args[0] = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }

    // La reconciliación de la visibilidad del wrapper ya no depende del timing de
    // este fetch: la maneja el MutationObserver instalado en init() (_watchWrapper),
    // que es la autoridad sobre la clase `hide` y no puede perder la carrera contra
    // el reset() de theme.js. Aquí solo transformamos la query.
    return _origFetch.apply(this, args);
  };

  // ════════════════════════════════════════════════════════════════════════
  // Intercepción del botón "Ver más" del predictivo
  // form.submit() (triggerSearch en theme.js) no dispara el evento 'submit',
  // por lo que se intercepta el click directamente en capture phase.
  // ════════════════════════════════════════════════════════════════════════
  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest ? evt.target.closest('[data-predictive-search-button]') : null;
    if (!btn) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    var form = document.getElementById('HeaderSearchForm');
    var searchInput = form && form.querySelector('input[type="search"]');
    var raw = searchInput ? searchInput.value.trim() : '';
    if (!raw) return;

    var stripped = _cleanQuery(raw);
    var corrected = SearchSynonyms.correctTypo(stripped);
    var base = corrected || stripped;

    SearchHistory.add(stripped);
    try {
      sessionStorage.setItem('hs-search-display', stripped);
      if (corrected) sessionStorage.setItem('hs-search-corrected', corrected);
      else sessionStorage.removeItem('hs-search-corrected');
    } catch (e) {}

    var expanded = SearchSynonyms.expand(base);
    var typeInput = form && form.querySelector('input[name="type"]');
    var type = typeInput ? typeInput.value : 'product';
    window.location.href = _searchUrl(expanded, type);
  }, true);

  // ════════════════════════════════════════════════════════════════════════
  // Init — espera DOMContentLoaded
  // ════════════════════════════════════════════════════════════════════════
  document.addEventListener('DOMContentLoaded', function () {
    var form = document.getElementById('HeaderSearchForm');
    var input = form && form.querySelector('input[type="search"]');
    if (!form || !input) return;

    // ── Intercepción del submit del formulario ───────────────────────────
    // Listener en document (capture phase) garantiza que corre ANTES que
    // cualquier handler registrado en el form por theme.js. Si se registra
    // en el form mismo, al ser "AT_TARGET" el orden es de registro y theme.js
    // gana porque carga primero.
    document.addEventListener('submit', function (evt) {
      var t = evt.target;
      var isHeaderForm  = t && t.id === 'HeaderSearchForm';
      var isSearchBar   = t && t.classList.contains('search-bar') && t.querySelector('input[name="q"]');
      if (!isHeaderForm && !isSearchBar) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      var searchInput = evt.target.querySelector('input[type="search"]');
      var raw = searchInput ? searchInput.value.trim() : '';
      if (!raw) return;

      // Quitar prefijos conversacionales ("busca X" → "X")
      var stripped = _cleanQuery(raw);

      // Corregir typo si existe
      var corrected = SearchSynonyms.correctTypo(stripped);
      var base = corrected || stripped;

      // Guardar en historial el término limpio
      SearchHistory.add(stripped);

      // Guardar en sessionStorage para mostrar en la página de resultados
      try {
        sessionStorage.setItem('hs-search-display', stripped);
        if (corrected) sessionStorage.setItem('hs-search-corrected', corrected);
        else sessionStorage.removeItem('hs-search-corrected');
      } catch (e) {}

      // Expandir con sinónimos para la búsqueda completa
      var expanded = SearchSynonyms.expand(base);

      // Construir URL respetando los parámetros del formulario
      var typeInput = evt.target.querySelector('input[name="type"]');
      var type = typeInput ? typeInput.value : 'product';
      window.location.href = _searchUrl(expanded, type);

    }, true); // capture en document → siempre primero

    // ── Panel de búsquedas recientes (dropdown propio y visible) ──────────
    // El SearchSuggestionsPanel original vive dentro de .igms-search-suggestions-b,
    // que está oculto (display:none) por un experimento Intelligems apagado, así
    // que el historial nunca se veía ahí. Creamos un dropdown propio que reutiliza
    // el estilo de .predictive-results y lo mostramos al enfocar el input vacío.
    var recentPanel = document.createElement('div');
    recentPanel.id = 'RecentSearchesPanel';
    recentPanel.className = 'predictive-results hide';
    recentPanel.innerHTML = '<div class="page-width"><div id="RecentSearchesInner"></div></div>';

    var predWrapForRecent = document.getElementById('PredictiveWrapper');
    if (predWrapForRecent && predWrapForRecent.parentNode) {
      // Hermano de #PredictiveWrapper: ambos son dropdowns absolutos y mutuamente
      // excluyentes (recientes cuando el input está vacío; predictivo al escribir).
      predWrapForRecent.parentNode.insertBefore(recentPanel, predWrapForRecent.nextSibling);
    } else {
      var searchContainer = document.querySelector('.site-header__search-container') || form.parentNode;
      if (searchContainer) searchContainer.appendChild(recentPanel);
    }
    var recentInner = recentPanel.querySelector('#RecentSearchesInner');

    function refreshRecent() {
      _renderHistoryPanel(recentInner, form, refreshRecent);
      if (SearchHistory.get().length === 0) recentPanel.classList.add('hide');
    }
    function showRecent() {
      if (input.value.trim() !== '') return;            // solo con el input vacío
      if (SearchHistory.get().length === 0) { recentPanel.classList.add('hide'); return; }
      _renderHistoryPanel(recentInner, form, refreshRecent);
      recentPanel.classList.remove('hide');
    }
    function hideRecent() { recentPanel.classList.add('hide'); }

    // Mostrar al enfocar el input (si está vacío)
    input.addEventListener('focus', showRecent);

    // Alternar según el contenido: vacío → recientes; con texto → ocultar
    input.addEventListener('input', function () {
      if (input.value.trim() === '') showRecent();
      else hideRecent();
    });

    // Al abrir la búsqueda desde la lupa el input recibe foco ~100ms después
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.js-search-header')) {
        setTimeout(showRecent, 120);
      }
    });

    // ── Autocompletado de términos en el predictivo ──────────────────────
    var predWrap = document.getElementById('PredictiveWrapper');
    if (predWrap) {
      var pw = predWrap.querySelector('.page-width') || predWrap;
      var acPanel = document.createElement('div');
      acPanel.id = 'PredictiveTermSuggestions';
      acPanel.style.display = 'none';

      var predResults = document.getElementById('PredictiveResults');
      if (predResults && predResults.parentNode === pw) pw.insertBefore(acPanel, predResults);
      else pw.insertAdjacentElement('afterbegin', acPanel);

      Autocomplete.panel = acPanel;
      Autocomplete.build();

      // Listener propio (debounce corto, instantáneo desde el diccionario local).
      // Independiente del keyup de theme.js que dispara la búsqueda de productos.
      var acTimeout;
      input.addEventListener('keyup', function (evt) {
        var k = evt.keyCode;
        if (k === 38 || k === 40 || k === 9 || k === 13) return; // flechas/tab/enter
        var val = input.value;
        clearTimeout(acTimeout);
        if (!val || val.trim().length < 2) { Autocomplete.clear(); return; }
        acTimeout = setTimeout(function () { Autocomplete.render(val); }, 100);
      });

      // Limpiar al vaciar el input (botón "x" del campo de búsqueda)
      input.addEventListener('search', function () {
        if (!input.value) { Autocomplete.clear(); showRecent(); }
      });

      // Autoridad sobre la visibilidad del wrapper.
      // theme.js le añade `hide` cada vez que la búsqueda predictiva de PRODUCTOS
      // devuelve 0 resultados (reset()), pero las sugerencias de término viven
      // DENTRO del wrapper, así que se ocultaban con él. El observer reacciona
      // sincrónicamente a ese cambio de clase —sin importar qué temporizador lo
      // provocó— y veta el `hide` si el input actual tiene sugerencias. Esto
      // elimina la carrera 100ms (términos) vs 500ms (productos) de raíz.
      _watchWrapper(predWrap);
    }

    // ── Página de resultados ─────────────────────────────────────────────
    if (_isSearchPage()) {
      _initSearchPageEnhancements();
    }
  });

  // ════════════════════════════════════════════════════════════════════════
  // _watchWrapper — hace a Autocomplete la autoridad sobre `#PredictiveWrapper.hide`
  //   theme.js oculta el wrapper (reset()) cuando hay 0 productos predictivos, lo
  //   que también ocultaba las sugerencias de término que viven dentro. El observer
  //   veta ese `hide` mientras el input actual tenga sugerencias, de forma
  //   determinista (no depende del orden de los temporizadores).
  // ════════════════════════════════════════════════════════════════════════
  var _wrapperGuard = false; // evita reentradas cuando nosotros mismos quitamos `hide`
  function _watchWrapper(wrapper) {
    if (!wrapper || typeof MutationObserver === 'undefined') return;

    var obs = new MutationObserver(function () {
      if (_wrapperGuard) return;
      // Solo nos importa cuando algo AÑADIÓ `hide`.
      if (!wrapper.classList.contains('hide')) return;
      if (!Autocomplete.hasLiveSuggestions()) return;

      _wrapperGuard = true;
      wrapper.classList.remove('hide');
      var sp = document.getElementById('SearchSuggestionsPanel');
      if (sp) sp.classList.add('hide');
      // Re-render por si reset() vació #PredictiveResults y el panel de términos.
      var form = document.getElementById('HeaderSearchForm');
      var input = form && form.querySelector('input[type="search"]');
      if (input) Autocomplete.render(input.value);
      _wrapperGuard = false;
    });

    obs.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Panel de búsquedas recientes — llena `inner` con el listado. La visibilidad
  // del dropdown que lo contiene la maneja el que llama (showRecent/hideRecent).
  // `onChange` se invoca tras borrar un término (individual o todos) para que el
  // que llama re-evalúe si debe ocultar el dropdown (historial vacío).
  // ════════════════════════════════════════════════════════════════════════
  function _renderHistoryPanel(inner, form, onChange) {
    if (!inner) return;
    var history = SearchHistory.get();

    if (history.length === 0) {
      inner.innerHTML = '';
      return;
    }

    var typeInput = form.querySelector('input[name="type"]');
    var type = typeInput ? typeInput.value : 'product';

    var items = history.map(function (term) {
      var expanded = SearchSynonyms.expand(SearchSynonyms.correctTypo(term) || term);
      var href = _searchUrl(expanded, type);
      var safeTerm = _esc(term);
      return '<li class="search-history__item">' +
        '<a href="' + href + '" class="search-history__link">' +
        '<svg class="search-history__icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<polyline points="12 8 12 12 14 14"/>' +
        '<path d="M3.05 11a9 9 0 1 0 .5-4.3"/>' +
        '<polyline points="3 3 3 8 8 8"/>' +
        '</svg>' +
        '<span>' + safeTerm + '</span></a>' +
        // Se pasa `term` CRUDO a _t y se escapa el resultado completo una vez.
        // Pasar safeTerm (ya escapado) produciría doble escape (&amp;quot;).
        '<button type="button" class="search-history__remove" data-term="' + safeTerm + '" aria-label="' +
        _esc(_t('recentRemove', 'Quitar [term] del historial', { term: term })) + '">' +
        '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' +
        '</svg></button>' +
        '</li>';
    }).join('');

    inner.innerHTML =
      '<p class="search-suggestions__heading search-history__heading">' +
      '<span>' + _esc(_t('recentHeading', 'Reciente')) + '</span>' +
      '<button type="button" class="search-history__clear-btn js-search-history-clear">' +
      _esc(_t('recentClear', 'Borrar')) + '</button>' +
      '</p>' +
      '<ul class="search-suggestions__list search-history__list">' + items + '</ul>';

    // Borrar todo
    var clearBtn = inner.querySelector('.js-search-history-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        SearchHistory.clear();
        inner.innerHTML = '';
        if (typeof onChange === 'function') onChange();
      });
    }

    // Borrar un término individual (X) y re-renderizar en el sitio.
    inner.querySelectorAll('.search-history__remove').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        SearchHistory.remove(btn.getAttribute('data-term'));
        if (typeof onChange === 'function') onChange();
        else _renderHistoryPanel(inner, form);
      });
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Mejoras en la página de resultados /search
  // ════════════════════════════════════════════════════════════════════════
  function _initSearchPageEnhancements() {
    var notice = document.getElementById('SearchCorrectionNotice');

    var displayTerm = '';
    var correctedTerm = '';
    try {
      displayTerm  = sessionStorage.getItem('hs-search-display') || '';
      correctedTerm = sessionStorage.getItem('hs-search-corrected') || '';
    } catch (e) {}

    // Registro AUTORITATIVO del historial: al correr en la página de resultados,
    // captura TODA búsqueda sin importar cómo se disparó (enter, botón "Ver más",
    // clic en una sugerencia de término, en una búsqueda reciente, buscador móvil,
    // enlaces externos, etc.). Los handlers de submit/click guardan de forma
    // optimista antes de navegar, pero este es el que garantiza que siempre quede
    // registrada la búsqueda que realmente se ejecutó. add() deduplica y la sube
    // al inicio, así que no hay problema si ya se guardó antes.
    try {
      var qParam = (new URLSearchParams(window.location.search).get('q') || '').replace(/\*/g, '').trim();
      // Preferir el término limpio guardado; si no, el primer término del OR expandido.
      var termToSave = displayTerm || (qParam.indexOf(' OR ') !== -1 ? qParam.split(' OR ')[0].trim() : qParam);
      termToSave = _cleanQuery(termToSave);
      if (termToSave) SearchHistory.add(termToSave);
    } catch (e) {}

    // Limpiar todos los campos .input-group-field que muestren la query expandida
    document.querySelectorAll('.search-bar input[name="q"], .input-group-field[name="q"]').forEach(function (field) {
      var val = field.value.replace(/\*/g, '').trim();
      if (val.indexOf(' OR ') !== -1) {
        // Preferir el término guardado; si no hay, usar el primer término del OR
        field.value = displayTerm || val.split(' OR ')[0].trim();
      }
    });

    // Mostrar banner de corrección si se detectó un typo
    if (notice && correctedTerm && displayTerm && correctedTerm !== displayTerm) {
      var params = new URLSearchParams(window.location.search);
      var type = params.get('type') || 'product';
      var originalUrl = _searchUrl(displayTerm, type, false);
      // La plantilla viene del archivo de locale (markup confiable); los valores
      // interpolados sí se escapan uno por uno antes de entrar.
      notice.innerHTML = '<span>' + _t(
        'didYouMean',
        'Mostrando resultados para <strong>[corrected]</strong>. ' +
        '¿Quisiste buscar <a href="[url]">[original]</a>?',
        { corrected: _esc(correctedTerm), url: originalUrl, original: _esc(displayTerm) }
      ) + '</span>';
      notice.style.display = '';
    }

    // Limpiar sessionStorage después de usarlo
    try {
      sessionStorage.removeItem('hs-search-display');
      sessionStorage.removeItem('hs-search-corrected');
    } catch (e) {}
  }

  // ════════════════════════════════════════════════════════════════════════
  // Utilidades
  // ════════════════════════════════════════════════════════════════════════
  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Escapa caracteres especiales de regex
  function _reEsc(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // ── Rutas localizadas ───────────────────────────────────────────────────
  // routes.search_url ya trae el prefijo de idioma ('/search', '/en/search',
  // '/en-us/search'). theme.routes se define en un <script> inline del <head>
  // de theme.liquid, así que siempre existe antes de que corra este archivo
  // (que carga con defer). El fallback a '/search' reproduce el comportamiento
  // previo si theme.routes faltara — piso de no-regresión.
  function _searchRoute() {
    var r = (window.theme && window.theme.routes && window.theme.routes.search) || '/search';
    return String(r).replace(/\/+$/, '') || '/search';
  }

  // Construye la URL de resultados. El '*' va FUERA del encode a propósito:
  // es el comodín de Shopify, no parte del término.
  function _searchUrl(term, type, withPrefixOption) {
    var u = _searchRoute() + '?q=' + encodeURIComponent(term) + '*&type=' + type;
    return withPrefixOption === false ? u : u + '&options%5Bprefix%5D=last';
  }

  // ¿Estamos en la página de resultados? Se compara contra la ruta localizada
  // en vez de contra '/search' literal, que daba false en /en/search y dejaba
  // sin correr todas las mejoras de la página de resultados.
  function _isSearchPage() {
    var here = window.location.pathname.replace(/\/+$/, '') || '/';
    return here === _searchRoute();
  }

  // Resalta en <strong> la parte de `display` que coincide con `qn` (ya normalizado).
  // _norm conserva la longitud (reemplazo 1:1), por lo que los índices son válidos
  // tanto sobre la versión normalizada como sobre la de display.
  function _highlight(display, qn) {
    if (!qn) return _esc(display);
    var dn = SearchSynonyms._norm(display);
    var i = dn.indexOf(qn);
    if (i === -1) return _esc(display);
    return _esc(display.slice(0, i)) +
      '<strong>' + _esc(display.slice(i, i + qn.length)) + '</strong>' +
      _esc(display.slice(i + qn.length));
  }

})();
